/* eslint-disable react/no-unknown-property -- Three.js properties */
import { useErrorBoundary } from 'use-error-boundary';
import React, { useRef, useState, useEffect, Suspense } from 'react';
import { Box, Typography } from '@mui/material';
import { Canvas, useThree } from '@react-three/fiber';
import { Environment, OrbitControls, Html, useProgress, ContactShadows } from '@react-three/drei';
import { useTranslation } from 'react-i18next';
import { log } from '@/logger/logger.ts';
import { Flex } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { Model } from './ThreedeeUtils';

const logger = log.getLogger('ThreeDeeViewer');

const VIEWER_SIZE = { w: 304, h: 304 }; // the reason for 304 is 320 (css custom-node.preview width) minus the 2x*8px padding

function ModelLoader() {
  const { progress } = useProgress();

  return <Html center>{progress} % loaded</Html>;
}

function SetCameraPosition({ cameraPosition }: { cameraPosition?: { x: number; y: number; z: number } }) {
  const { camera } = useThree();

  useEffect(() => {
    // Only set camera position if explicitly provided
    if (cameraPosition) {
      camera.position.x = cameraPosition.x ?? -3;
      camera.position.y = cameraPosition.y ?? 4;
      camera.position.z = cameraPosition.z ?? 5;
    } else {
      // Set default positions only on initial mount
      camera.position.x = -3;
      camera.position.y = 4;
      camera.position.z = 5;
    }

    camera.updateProjectionMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function CameraControls({ lockOrbit, setCameraPosition }) {
  const { camera } = useThree();

  return (
    <OrbitControls
      enableRotate={!lockOrbit}
      enableZoom={!lockOrbit}
      enablePan={!lockOrbit}
      target={[0, 1.5, 0]}
      dampingFactor={0.1}
      onChange={() => {
        if (setCameraPosition) {
          setCameraPosition({
            x: camera.position.x,
            y: camera.position.y,
            z: camera.position.z,
          });
        }
      }}
    />
  );
}

type ThreeDeeViewerProps = {
  objUrl: string;
  containerSize?: { w: number; h: number };
  lockOrbit?: boolean;
  cameraPosition?: { x: number; y: number; z: number };
  setCameraPosition?: (position: { x: number; y: number; z: number }) => void;
  allowInteraction?: boolean;
  displayBackground?: boolean;
};

function ThreeDeeViewer({
  objUrl,
  containerSize,
  lockOrbit,
  cameraPosition,
  setCameraPosition,
  allowInteraction = true,
  displayBackground = false,
}: ThreeDeeViewerProps) {
  const { t } = useTranslation();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewerSize = containerSize || VIEWER_SIZE;
  const { ErrorBoundary, didCatch, error, reset: resetError } = useErrorBoundary();

  useEffect(() => {
    let url: string | null = null;
    if (objUrl) {
      fetch(objUrl)
        .then((response) => response.blob())
        .then((blob) => {
          url = URL.createObjectURL(blob);
          setObjectUrl(url);
        })
        .catch((error) => logger.error('Error fetching OBJ file', error));
    }

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [objUrl]);

  useEffect(() => {
    if (containerRef.current) {
      const canvasElement = containerRef.current;

      // Focus canvas when mouse enters or interaction occurs
      const handleMouseEnter = () => {
        canvasElement.focus();
      };

      const handleWheel = (event) => {
        const containerElement = containerRef.current;
        if (
          containerElement &&
          (containerElement === document.activeElement || containerElement.contains(document.activeElement))
        ) {
          event.stopPropagation(); // Prevent scrolling propagation
        }
      };
      const handleMouseClick = (event) => {
        const containerElement = containerRef.current;
        if (
          containerElement &&
          (containerElement === document.activeElement || containerElement.contains(document.activeElement))
        ) {
          event.stopPropagation(); // Prevent scrolling propagation
        }
      };

      if (allowInteraction) {
        canvasElement.addEventListener('mouseenter', handleMouseEnter);
        canvasElement.addEventListener('click', handleMouseClick);
        canvasElement.addEventListener('wheel', handleWheel);
      }

      return () => {
        canvasElement.removeEventListener('mouseenter', handleMouseEnter);
        canvasElement.removeEventListener('click', handleMouseClick);
        canvasElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [containerRef, allowInteraction]);

  const setCanvasSize = () => {
    if (canvasRef && canvasRef.current) {
      canvasRef.current.style.width = `${viewerSize.w}px`;
      canvasRef.current.style.height = `${viewerSize.h}px`;
    }
  };

  const handleCanvasCreated = () => {
    setCanvasSize();
  };

  useEffect(() => {
    setCanvasSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objUrl]);

  useEffect(() => {
    if (error) {
      logger.error('Error in Three.js boundary', error);
      resetError();
    }
  }, [error, resetError, t]);

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      className={`3d_player ${displayBackground ? 'media-container-dark' : ''}`}
      id="three-d-player-wrapper"
      sx={{
        width: `${viewerSize.w}px`,
        height: `${viewerSize.h}px`,
        '&:focus': {
          outline: 'none',
        },
      }}
    >
      {didCatch ? (
        <Flex sx={{ padding: 2 }}>
          <Typography variant="body-sm-rg" color="error">
            {t(I18N_KEYS.COMMON_COMPONENTS.THREE_DEE_VIEWER.ERROR)}
          </Typography>
        </Flex>
      ) : objectUrl ? (
        <ErrorBoundary>
          <Canvas
            key={objectUrl}
            shadows
            onCreated={({ gl }) => {
              gl.setClearColor('#e0e0e0', 0);
              handleCanvasCreated();
            }}
            // this fixes canvas size issues caused by the zoom of the workflow stage which breaks the automatic
            // measurement of the canvas container
            resize={{ offsetSize: true }}
            gl={{ preserveDrawingBuffer: true, alpha: true }}
            ref={canvasRef}
          >
            <SetCameraPosition cameraPosition={cameraPosition} />
            <CameraControls lockOrbit={allowInteraction ? lockOrbit : true} setCameraPosition={setCameraPosition} />
            <directionalLight
              intensity={1}
              position={[-5, 10, -5]}
              castShadow
              shadow-mapSize-height={512}
              shadow-mapSize-width={512}
            />
            <ambientLight color="#dce7f5" intensity={0.5} />
            <Suspense fallback={<ModelLoader />}>
              <Model objUrl={objectUrl} type={4} />
              <ContactShadows
                rotation-x={Math.PI / 2}
                position={[0, 0, 0]}
                opacity={1}
                width={5}
                height={5}
                blur={1}
                far={1}
              />
            </Suspense>
            <Environment preset="city" blur={0.5} />
          </Canvas>
        </ErrorBoundary>
      ) : null}
    </Box>
  );
}

export default ThreeDeeViewer;
